// https://supabase.com/docs/guides/auth/auth-anonymous
import { View, Text, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { Button, Toast } from 'native-base';
import { useState, useEffect } from 'react';
import { Session } from '@supabase/supabase-js';
import Entypo from '@expo/vector-icons/Entypo';
import { useNavigation } from '@react-navigation/native';

import { supabase } from '../lib/supabase';
import { db } from '../data/database';
import { useTypedSelector } from '../redux/store';
import { UserAPI } from '../data/api';
import { useDispatch } from 'react-redux';
import { setUpdateUserProfily, setUserInfo } from '../redux/reducers/authSlice';
import { ActivityTip } from '../components/tips/activityTip';

export default function ProfileScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const navigation = useNavigation();
  const userCurrent = useTypedSelector(state => state.userCurrent);
  const dispatch = useDispatch();

  const [activityOpen, setActivityOpen] = useState(false);
  
  useEffect(() => {
    const initializeSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
    };
    
    initializeSession();

    supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
  }, []);

  const signInWithEmail = async () => {
    if (!email || !password) {
      Toast.show({
        title: 'Please fill in all fields',
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
      return;
    }

    if (!validateEmail(email)) {
      Toast.show({
        title: 'Please enter a valid email address',
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      Toast.show({ title: error.message, placement: "bottom", bgColor: "rgba(255,0,0,0.4)" });
    }else{
      UserAPI.getCurrentUser(dispatch);
      setActivityOpen(true);
      if(data.user){
        dispatch(setUserInfo(data.user))
      }
    }
    setLoading(false);
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string) => {
    if (password.length < 6) return 'Password must be at least 6 characters long';
    // if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    // if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    // if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  async function signUpWithEmail() {
    if (!email || !password) {
      Toast.show({
        title: 'Please fill in all fields',
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
      return;
    }

    if (!validateEmail(email)) {
      Toast.show({
        title: 'Please enter a valid email address',
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      Toast.show({
        title: passwordError,
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
      return;
    }

    setLoading(true);
    const deviceId = await db.getDeviceId();
    try {
      if (session && session.user.is_anonymous ) {
        // Update anonymous user with email
        const { data, error: updateError } = await supabase.auth.updateUser({ 
          email,
          data: {
            device_id: deviceId,
            invitation_code: userCurrent.be_invited, // Set this to the invitation code if needed
          }
        });

        if (updateError) throw updateError;
        UserAPI.getCurrentUser(dispatch);
        setActivityOpen(true);
        if(data.user){ dispatch(setUserInfo(data.user)) }
        Toast.show({
          title: 'Please check your email to verify your account',
          placement: "bottom",
          bgColor: "rgba(0,255,0,0.4)"
        });
      } else {
        // Regular sign up for non-anonymous users
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              device_id: deviceId,
              invitation_code: userCurrent.be_invited, // Set this to the invitation code if needed
            }
          }
        });

        if (error) throw error;

        UserAPI.getCurrentUser(dispatch);
        setActivityOpen(true);
        if(data.user){ dispatch(setUserInfo(data.user)) }
        Toast.show({
          title: 'Check your email for the confirmation link!',
          placement: "bottom",
          bgColor: "rgba(0,255,0,0.4)"
        });
      }
    } catch (error: any) {
      let errorMessage = error.message;
      // Handle specific Supabase error cases
      if (error.message.includes('unique constraint')) {
        errorMessage = 'This email is already registered';
      } else if (error.message.includes('rate limit')) {
        errorMessage = 'Too many attempts. Please try again later';
      }
      Toast.show({
        title: errorMessage,
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
    }
    setLoading(false);
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      Toast.show({
        title: error.message,
        placement: "bottom",
        bgColor: "rgba(255,0,0,0.4)"
      });
    }else{
      dispatch(setUserInfo(null))
      dispatch(setUpdateUserProfily({
        coins: 0, id: "", created_at: "", sub_list: [],
        unlock_images: {}, subscription: [], phone_time: 0, unlock_theme: [],
        be_invited: null
      }))
    }
  };

  const tipChange = ()=>{
    let state = navigation.getState();
    setActivityOpen(false);
    if(state){
      let arr = state.routes;
      if(arr.length > 1){
        let last = arr[arr.length - 2];
        if(last.name === "Activity"){
          navigation.goBack();
        }else{
          navigation.replace("Activity", {});
        }
      }
    }
    // navigation.goBack();
  }

  const goSupport = ()=>{
    navigation.navigate("Support", {});
  }

  return (
    <View style={styles.container}>
      <Button style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        borderRadius: 100, position: "absolute",
        top: 10, left: 10, padding: 4, zIndex: 9,
      }} onPress={()=> navigation.goBack() }>
        <Entypo name="chevron-left" size={24} color="#FFFFFF" />
      </Button>
      {session?.user && !session.user.is_anonymous ? (
        <>
          <Text style={styles.header}>Welcome {session.user.email}</Text>
          <TouchableOpacity
            style={[styles.button, styles.buttonOutline]}
            onPress={signOut}
            disabled={loading}
          >
            <Text style={styles.buttonOutlineText}>Sign Out</Text>
          </TouchableOpacity>
        </>
      ) : (
        <>
          <Text style={styles.header}>Sign in or create an account</Text>
          <View style={styles.form}>
            <TextInput
              style={styles.input}
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoComplete="email"
              keyboardType="email-address"
              textContentType="emailAddress"
            />
            
            <TextInput
              style={styles.input}
              placeholder="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoComplete="password"
              textContentType="password"
            />

            <TouchableOpacity
              style={styles.button}
              onPress={signInWithEmail}
              disabled={loading}
              accessibilityRole="button"
              accessibilityLabel="Sign in"
            >
              <Text style={styles.buttonText}>
                {loading ? 'Loading...' : 'Sign in'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.buttonOutline]}
              onPress={signUpWithEmail}
              disabled={loading}
              accessibilityRole="button"
              accessibilityLabel="Sign up"
            >
              <Text style={styles.buttonOutlineText}>
                {loading ? 'Loading...' : 'Sign up'}
              </Text>
            </TouchableOpacity>
          </View>
        </>
      )}
      <View style={{
        flexDirection: "row", justifyContent: "center", padding: 10,
        gap: 8
      }}>
        <Text style={{ color: "#DDDDDD" }}>
          Feedback issue
        </Text>
        <Text style={{ color: "rgb(0, 122, 255)" }}
          onPress={goSupport}>
          Support
        </Text>
      </View>
      <ActivityTip open={activityOpen} setOpen={()=> setActivityOpen(false) }
        onChange={tipChange} />
    </View>
  );
}

const styles = StyleSheet.create({
  form: {
    width: '100%',
  },
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#191B2C',
    padding: 20,
  },
  header: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#EEE',
    marginBottom: 30,
  },
  input: {
    width: '100%',
    height: 50,
    borderWidth: 1,
    borderColor: '#ddd',
    color: "#FFF",
    borderRadius: 8,
    paddingHorizontal: 15,
    marginBottom: 15,
    fontSize: 16,
  },
  button: {
    width: '100%',
    height: 50,
    backgroundColor: '#007AFF',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonOutline: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#007AFF',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  buttonOutlineText: {
    color: '#007AFF',
    fontSize: 16,
    fontWeight: 'bold',
  },
});